/**
 * useFinancialData Hook
 *
 * Consolidates financial data fetching logic for security analysis pages.
 * Handles dev/prod environments, free/paid access, and caching.
 */
import { useState, useEffect, useCallback } from 'react';
import axios from 'axios';
import { useAuth0 } from '@auth0/auth0-react';
import { handleSessionExpired } from '../utils/authHelpers';
import { loadLocalOrFixtureJson, shouldUseLocalOrFixtureData } from '../utils/dataLoader';
import {
  FREE_SECURITY_ANALYSIS_DATASETS,
  getFreeSecurityAnalysisCdnUrl,
  getSecurityAnalysisSampleTicker,
  isFreeSecurityAnalysisTicker,
} from '../constants/api';
import { isPrivateByTicker } from '../utils/companyHelpers';
import useSubscriptionStatus from './useSubscriptionStatus';

// Key stats filter criteria for sorting and filtering
const KEY_STATS_FILTER_CRITERIA = [
  'Market Cap',
  'Enterprise Value',
  'EV / Revenue',
  'EV / GM',
  'Price / FCF',
  'Close Date',
];

// API endpoints
const API_URL = 'https://mwl26uv0m7.execute-api.us-east-1.amazonaws.com/prod/validate';
/**
 * Custom hook for fetching and managing financial data for a security.
 *
 * @param {string} ticker - Stock ticker symbol
 * @param {Object} options - Configuration options
 * @param {boolean} options.enabled - Whether to enable data fetching (default: true)
 * @returns {Object} Financial data and state
 */
export function useFinancialData(ticker, options = {}) {
  const { enabled = true } = options;

  // Auth state
  const { getAccessTokenSilently } = useAuth0();
  const { isPaid, isAuthenticated } = useSubscriptionStatus();

  // Data state
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [annualData, setAnnualData] = useState([]);
  const [qtrData, setQtrData] = useState([]);
  const [keyStats, setKeyStats] = useState([]);
  const [annualMapping, setAnnualMapping] = useState([]);
  const [qtrMapping, setQtrMapping] = useState([]);
  const [reportingCurrency, setReportingCurrency] = useState('USD');
  const [segmentData, setSegmentData] = useState(null);
  const [valueQualifiers, setValueQualifiers] = useState([]);

  const normalizedTicker = String(ticker).toUpperCase();
  const isPrivateCompany = options.isPrivate ?? isPrivateByTicker(normalizedTicker) === true;
  const freeSecurityAnalysisTicker = isFreeSecurityAnalysisTicker(normalizedTicker);
  const sampleTicker = getSecurityAnalysisSampleTicker({
    ticker: normalizedTicker,
    isPrivate: isPrivateCompany,
  });
  const gateActive = !isPaid && !freeSecurityAnalysisTicker;

  // Apply financial data helper
  const applyFinancialData = useCallback((financialData) => {
    const dynamicDataAnnual = financialData.annualData;
    const dynamicDataQtr = financialData.quarterlyData;
    const dynamicKeyStats = financialData.keyStats;

    if (Array.isArray(dynamicKeyStats)) {
      const sortedKeyStats = [...dynamicKeyStats].sort((a, b) => {
        const indexA = KEY_STATS_FILTER_CRITERIA.indexOf(a.field);
        const indexB = KEY_STATS_FILTER_CRITERIA.indexOf(b.field);
        return (indexA === -1 ? Infinity : indexA) - (indexB === -1 ? Infinity : indexB);
      });
      setKeyStats(sortedKeyStats.filter(item =>
        KEY_STATS_FILTER_CRITERIA.includes(item.field)
      ));
    } else {
      console.error('Unexpected data format: keyStats', dynamicKeyStats);
    }

    if (Array.isArray(dynamicDataAnnual?.data)) {
      setAnnualData(dynamicDataAnnual.data);
      setAnnualMapping(dynamicDataAnnual.mappingTable);
    } else {
      console.error('Unexpected data format: annualData', dynamicDataAnnual);
    }

    if (Array.isArray(dynamicDataQtr?.data)) {
      setQtrData(dynamicDataQtr.data);
      setQtrMapping(dynamicDataQtr.mappingTable);
      setReportingCurrency(dynamicDataQtr.currency || 'USD');
    } else {
      console.error('Unexpected data format: quarterlyData', dynamicDataQtr);
    }

    if (financialData.segmentData) {
      setSegmentData(financialData.segmentData);
    }

    setValueQualifiers(
      Array.isArray(financialData.valueQualifiers) ? financialData.valueQualifiers : []
    );
  }, []);

  // Fetch data
  useEffect(() => {
    if (!enabled || !ticker) return;

    const fetchAuthData = async () => {
      // === LOCAL DEVELOPMENT MODE ===
      if (shouldUseLocalOrFixtureData()) {
        setLoading(true);
        setError(null);

        try {
          const financialData = await loadLocalOrFixtureJson(`data/stockpage/financial/${normalizedTicker}.json`);
          applyFinancialData(financialData);
        } catch (err) {
          console.error(`Error loading local stockpage/${ticker}.json`, err);
          setError(err);
        } finally {
          setLoading(false);
        }

        return;
      }

      // === PRODUCTION MODE ===
      // Free users always see the appropriate sample data: MDB for public, PVT-DBRK for private.
      if (!isPaid) {
        setLoading(true);
        setError(null);
        try {
          const url = getFreeSecurityAnalysisCdnUrl(sampleTicker, FREE_SECURITY_ANALYSIS_DATASETS.FINANCIALS);
          const { data } = await axios.get(url);
          applyFinancialData(data);
        } catch (err) {
          console.error('Error fetching free financial data', err);
          setError(err);
        } finally {
          setLoading(false);
        }
        return;
      }

      setLoading(true);
      setError(null);

      try {
        const token = await getAccessTokenSilently({
          audience: 'https://api.softwareiq.io',
          scope: 'read:news',
        });

        const response = await axios.get(
          `${API_URL}?fileKey=securityanalysis/financial/${normalizedTicker}.json`,
          {
            headers: {
              'Content-Type': 'application/json',
              Authorization: `Bearer ${token}`,
            },
          }
        );

        const financialData = response.data.data;
        applyFinancialData(financialData);
      } catch (err) {
        console.error('Error fetching financial data', err);
        setError(err);
        if (
          err.message?.includes('Missing Refresh Token') ||
          err.error === 'login_required' ||
          err?.response?.status === 401
        ) {
          handleSessionExpired();
        }
      } finally {
        setLoading(false);
      }
    };

    fetchAuthData();
  }, [enabled, ticker, normalizedTicker, isPaid, isAuthenticated, getAccessTokenSilently, applyFinancialData, freeSecurityAnalysisTicker, sampleTicker]);

  // Determine if revenue scale should be billions
  const shouldUseBillionsScale = useCallback(() => {
    if (annualData.length === 0) return false;
    const revenueData = annualData.find(item => item.field === 'Revenue');
    if (revenueData) {
      const lastRevenue = revenueData[Object.keys(revenueData).pop()];
      return lastRevenue > 2000000000;
    }
    return false;
  }, [annualData]);

  return {
    // Data
    annualData,
    qtrData,
    keyStats,
    annualMapping,
    qtrMapping,
    reportingCurrency,
    segmentData,
    valueQualifiers,

    // State
    loading,
    error,
    gateActive,

    // Auth info
    isPaid,
    isAuthenticated,

    // Helpers
    shouldUseBillionsScale,
  };
}

export default useFinancialData;
